


import { useEffect, useRef, useState } from "react";
import { Popup } from "@/components/common/Popup";

import { useApiFetch } from "@/helper/apiFetch";

import { useTranslate } from "@/helper/translate";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { set, z } from "zod";
import { Dropdown } from "@/components/ui/filterSelect";
import { OptionSet } from "@/components/ui/optionset";
import { Toggle } from "@/components/ui/toggle";
import { useSearchParams } from "react-router-dom";
import { EventSchema } from "@/components/event/EventSchema";
import { Delete, ExternalLink, Loader2, Pencil } from "lucide-react";
import { TagBox } from "@/components/ui/tagbox";
import { UserManagement } from "@/components/event/userManagement";
import { Checkbox } from "@/components/ui/checkbox";
import { PlenumSetupPopup } from "@/components/plenum/PlenumSetupPopup";
import { AgendaSetupPopup } from "@/components/agenda/AgendaSetupPopup";
import { EventFeatureModel } from "@/components/event/EventSchema";
import { useAppHub } from "@/realtime/AppHubProvider";
import { DocumentCenterPopup } from "@/components/document/DocumentCenterPopup";
import { DatePickerInput } from "@/components/ui/date";
import { NumberInput } from "@/components/ui/number";
import { DeleteDataPopup } from "@/components/event/DeleteDataPopup";
import { TextVariables } from "@/components/event/TextVariables";
import { flattenValidationErrors } from "@/helper/common";
import { TextChatSetupPopup } from "@/components/textchat/TextChatSetupPopup";


const featureItems = [
  { name: "Plenum" as const, label: "Plenum", hasSetup: true },
  { name: "TextChat" as const, label: "Text Chat", hasSetup: true },
  { name: "Agenda" as const, label: "Agenda", hasSetup: true },
  { name: "DocumentCenter" as const, label: "Document Center", hasSetup: true },
  { name: "ContactForm" as const, label: "Contact Form", hasSetup: false },
  { name: "EventTracking" as const, label: "Event Tracking", hasSetup: false },
  // { name: "Networking" as const, label: "Networking" , hasSetup: true },
  // { name: "WatchTogether" as const, label: "Watch Together" , hasSetup: true },
  // { name: "Marketplace" as const, label: "Marketplace" , hasSetup: true },
  // { name: "Showroom" as const, label: "Showroom" , hasSetup: true },
  // { name: "Workshops" as const, label: "Workshops" , hasSetup: true },
  // { name: "HelpDesk_VideoChatroomId" as const, label: "Help Desk Video Chatroom" , hasSetup: true },
  // { name: "Games" as const, label: "Games" , hasSetup: true },
  { name: "WorldMap" as const, label: "World Map", hasSetup: false },
];

const trackingOptions = [
  { label: "None", value: 0 },
  { label: "Partial", value: 1 },
  { label: "Full", value: 2 },
] as const;

export function SetupPage() {

  const { connection, isConnected: isHubConnected } = useAppHub();

  const form = useForm<z.infer<typeof EventSchema>>({
    resolver: zodResolver(EventSchema),
    defaultValues: {}
  });

  const bannerInputRef = useRef<HTMLInputElement>(null);

  const { translate } = useTranslate();

  const [params, setParams] = useSearchParams();
  const [eventId, setEventId] = useState(params.get("eventId") ?? "");
  const isNew = params.get("new") === "true";

  const [isEditMode, setEditMode] = useState(eventId === "" || isNew);
  const [isLoaded, setIsLoaded] = useState(false);
  type Lookup = { Id: number; Value: string; Name: string };

  const [languages, setLanguages] = useState<Lookup[]>([]);
  const [templates, setTemplates] = useState<Lookup[]>([]);
  const [linkTemplates, setLinkTemplates] = useState<Lookup[]>([]);
  const lobbyTypes: Lookup[] = [
    { Value: "None", Id: 0, Name: "" },
    { Value: "Dynamic", Id: 1, Name: "" },
    { Value: "Static", Id: 2, Name: "" },
    { Value: "iSpring", Id: 3, Name: "" },
    { Value: "Agenda", Id: 4, Name: "" }
  ];

  const linkTypes: Lookup[] = [
    { Value: "None", Id: 0, Name: "" },
    { Value: "Link", Id: 1, Name: "" },
    { Value: "Template", Id: 2, Name: "" }
  ];

  
const tabs = [
  { key: "main", label: "Main" },
  { key: "branding", label: "Branding" },
  { key: "access", label: "Access" },
  { key: "features", label: "Features" },
  { key: "links", label: "Links" },
  { key: "variables", label: "Text Variables" }];

  const [tabIndex, setIndex] = useState(0);
  const [errorText, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [eventFeature, setEventFeature] = useState<EventFeatureModel>({} as EventFeatureModel);
  const [featureFetched, setFeatureFetched] = useState(false);
  const [linksHtml, setLinksHtml] = useState("");
  const [linksFetched, setLinksFetched] = useState(false);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [popupName, setPopup] = useState("");
  const isLanguageSwitchEnabled = !!form.watch("LanguageSwitch");
  const isNavigationEnabled = !!form.watch("AllowNavigation");





  const apiFetch = useApiFetch();




  const handleBannerFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!eventId) {
      setError("Missing event id");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError(translate("ui.fileTooLarge"));
      return;
    }

    setIsUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("myFile", file);
      fd.append("eventId", eventId);

      let res = await apiFetch(`/api/EventPool/UploadEventBanner`, { method: "POST", body: fd });


      if (!res.ok) {
        const eText = await res.text();
        setError(eText || "Banner upload failed");
        return;
      }

      let bannerUrl = "";
      const contentType = res.headers.get("content-type") ?? "";

      if (contentType.includes("application/json")) {
        const data = await res.json();
        bannerUrl = typeof data === "string"
          ? data
          : String(data?.url ?? data?.Url ?? data?.bannerUrl ?? data?.BannerUrl ?? "");
      } else {
        bannerUrl = (await res.text()).trim();
      }

      if (!bannerUrl) {
        setError("Banner upload succeeded but no URL was returned");
        return;
      }

      form.setValue("Banner", bannerUrl, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    } catch (err) {
      console.error(err);
      setError("Banner upload failed");
    } finally {
      setIsUploadingBanner(false);
    }
  };




  const onSubmit = async (values: z.infer<typeof EventSchema>) => {
    try {
      setIsSaving(true);
      setError("");
      Save(values);


    } catch (err) {
      console.error(err);
    }
  }

  const submitSetupForm = form.handleSubmit(onSubmit, (errors) => {
    const lines = flattenValidationErrors(errors);
    const message = lines.length > 0
      ? `Please fix the following fields:\n\n${lines.join(", ")}`
      : "Please check your inputs and try again.";

    setError(message);
  });



  const Save = async (values: z.infer<typeof EventSchema>) => {
    try {


      const fd = new FormData();
    
      fd.append("key", eventId);
      fd.append("values", JSON.stringify(values));


      const res = await apiFetch(`/api/eventpool/${eventId==""?"PostNewEvent":"PutEvent"}`, { method: eventId==""?"POST":"PUT", body: fd });

      setIsSaving(false);
      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);

      }
      else {
        if (eventId == "") {
          const d = await res.text();
          const newEventId = d.replaceAll("\"", "");
          setEventId(newEventId);
          setParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("eventId", newEventId);
            next.delete("new");
            return next;
          }, { replace: true });
          form.setValue("Id", newEventId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

        }




      }
    } catch (err) {
      console.error(err);
      setIsSaving(false);
    }
  }


  const SetLive = async (value: boolean) => {

 if (!connection || !isHubConnected) 
      throw new Error("Hub is not connected");

    try {

      await connection.invoke("OnAdminActivateEvent", eventId, value, false);
      form.setValue("Live", value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    }
    catch (err) {
      console.error(err);

      setError(translate("ui.failedsave") + err);

    }


  };


  const handleFeatureToggle = async (featureName: keyof EventFeatureModel, value: boolean) => {
    const previousFeature = eventFeature;
    const updatedFeature: EventFeatureModel = {
      ...previousFeature,
      EventId: (previousFeature.EventId ?? eventId ?? form.getValues("Id") ?? "") as string,
      [featureName]: value,
    };

    setEventFeature(updatedFeature);

    try {
      const fd = new FormData();
      for (const [key, rawValue] of Object.entries(updatedFeature)) {
        const fieldValue = rawValue == null ? "" : String(rawValue);
        fd.append(key, fieldValue);
      }

      const res = await apiFetch(`/api/eventpool/PutEventFeatures`, { method: "POST", body: fd });

      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);
        setEventFeature(previousFeature);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save feature");
      setEventFeature(previousFeature);
    }
  };

  const handleTrackingChange = async (value: number | boolean) => {
    const trackingMode = Number(value);
    const previousFeature = eventFeature;
    const updatedFeature: EventFeatureModel = {
      ...previousFeature,
      EventId: (previousFeature.EventId ?? eventId ?? form.getValues("Id") ?? "") as string,
      EventTrackingPartial: trackingMode === 1,
      EventTrackingFull: trackingMode === 2,
    };

    setEventFeature(updatedFeature);

    try {
      const fd = new FormData();
      for (const [key, rawValue] of Object.entries(updatedFeature)) {
        const fieldValue = rawValue == null ? "" : String(rawValue);
        fd.append(key, fieldValue);
      }

      const res = await apiFetch(`/api/eventpool/PutEventFeatures`, { method: "POST", body: fd });

      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);
        setEventFeature(previousFeature);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save feature");
      setEventFeature(previousFeature);
    }
  };

  useEffect(() => {




    apiFetch(`/Admin/GetEvent?eventId=${eventId}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then(data => {


        form.reset(data.e);
        setTemplates((data.templates));
        setLanguages(data.languages ?? []);
        setLinkTemplates(data.list ?? []);



        setIsLoaded(true);



      })
      .catch(err => {
        console.error(err);

      });
  }, []);


  useEffect(() => {


    if (tabIndex == 3 && !featureFetched) {

      apiFetch(`/Admin/GetEventFeatures?eventId=${eventId}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch");
          return res.json();
        })
        .then(data => {


          form.reset(data.e);
          setEventFeature((data));
          setFeatureFetched(true);


        })
        .catch(err => {
          console.error(err);

        });
    }
  }, [tabIndex, featureFetched]);

  useEffect(() => {
    if (tabIndex !== 4 || linksFetched || !eventId) {
      return;
    }

    setIsLoadingLinks(true);
    apiFetch(`/Admin/GetEventLinks?eventId=${eventId}&newCode=true`)
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(await res.text());
        }

        return res.text();
      })
      .then((html) => {
        setLinksHtml(html);
        setLinksFetched(true);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load event links");
      })
      .finally(() => {
        setIsLoadingLinks(false);
      });
  }, [apiFetch, eventId, linksFetched, tabIndex]);


  return (

    <div className="p-5">
      {!isLoaded ? (
        <div>Loading…</div>
      ) : (
        <div>
          <input
            type="file"
            accept="image/*"
            ref={bannerInputRef}
            style={{ display: "none" }}
            onChange={handleBannerFileChange}
          />

          <Form {...form}>

            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
                  e.preventDefault();
                }
              }}
            >

              {!isEditMode ? (
                <div className="flex gap-x-2.5 justify-between">
                  <div className=" p-4 rounded-md bg-[#f1f1f1]">
                    <div className="flex gap-x-2.5 ">

                      <div className="text-[#d22] text-lg font-bold">{form.getValues("Name")}</div>
                      <div >(
                        <a
                          href={`https://one.beamstream.eu/${form.getValues("EventPath")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1"
                        >
                          {form.getValues("EventPath")}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        )</div>
                      <div className="ml-[30px]">
                        <Button type="button" onClick={() => setEditMode(true)} variant="outline" size="icon" className="h-7 w-7" aria-label="Edit role identifier">
                          <Pencil className="h-3 w-3" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-x-2.5 ">
                      <div>{form.getValues("DateFrom")
                        ? new Intl.DateTimeFormat("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).format(new Date(form.getValues("DateFrom")))
                        : ""}</div> until
                      <div>{new Date(form.getValues("DateFrom")).getDate() != new Date(form.getValues("DateTo")).getDate()
                        ? new Intl.DateTimeFormat("de-DE", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).format(new Date(form.getValues("DateTo")))
                        : new Intl.DateTimeFormat("de-DE", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).format(new Date(form.getValues("DateTo")))}</div>

                    </div>
                  </div>
                  <div className="p-4 rounded-md bg-[#f1f1f1] ">
                  <div className="flex justify-center items-center "><Toggle
                    title="Live"
                    className="!text-xl"
                    checked={!!form.getValues("Live")}
                    onChange={(e) => SetLive(e.target.checked)}
                  />
                </div>
                  <Button type="button" onClick={() => setIsDeleting(true)} className="mt-2">Daten löschen...</Button></div>

                </div>) : (
                <div className="flex gap-x-2.5 ">
                  <FormField
                    control={form.control}
                    name="Name"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="EventPath"
                    render={({ field }) => (
                      <FormItem >
                        <FormLabel>Path</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />


                  <FormField
                    control={form.control}
                    name="DateFrom"
                    render={({ field }) => (
                      <FormItem >
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <DatePickerInput {...field}
                            
                            showTime={true} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="DateTo"
                    render={({ field }) => (
                      <FormItem >
                        <FormLabel>End Date</FormLabel>
                        <FormControl>
                          <DatePickerInput {...field}
                           
                            showTime={true} />
                        </FormControl>
                      </FormItem>
                    )}
                  />






                </div>
              )}


              <div className="!mt-0">

                <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#ddd]">
                  <div className="tabs mt-0 flex-1 !border-b-0">
                    {tabs.map((tab, index) => (
                     (eventId=="" && index > 0) ? null :(
                      <span
                        key={index}
                        className={`tab ${index === tabIndex ? 'active' : ''}`}
                        onClick={() => setIndex(index)}
                      >
                        {translate(`ui.${tab.key}`)}
                      </span>)
                    ))}
                  </div>
                  {tabIndex != 3 && tabIndex != 4 && tabIndex != 5 && (
                    <Button type="button" className="mb-2 shrink-0 !bg-primary !text-primary-foreground hover:!bg-primary/90" onClick={() => void submitSetupForm()}>
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                      {translate("common.save")}
                    </Button>)}
                </div>

                <div >






                  <div className="" key={tabIndex}>
                    {tabIndex == 0 && (

                      <div>
                        <div className='flex flex-wrap gap-y-8 gap-x-5 items-center mt-2'>




                          <FormField
                            control={form.control}
                            name="DefaultLanguage"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Default Language</FormLabel>
                                <FormControl>
                                  <Dropdown
                                    {...field}
                                    source={languages}
                                    getLabel={(x) => x.Value}
                                    getValue={(x) => x.Value}
                                    searchable={true}
                                    disabled={false}

                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="LanguageSwitch"
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormControl>
                                  <Toggle
                                    title="Language Switch"
                                    checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    ref={field.ref} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          {isLanguageSwitchEnabled && (
                            <FormField
                              control={form.control}
                              name="Languages"
                              render={({ field }) => (
                                <FormItem className="">
                                  <FormLabel>Languages</FormLabel>
                                  <FormControl>
                                    <TagBox
                                      source={languages}
                                      value={field.value}
                                      getLabel={(x) => x.Value}
                                      getValue={(x) => x.Value}
                                      onChange={field.onChange}

                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />)}

                          <FormField
                            control={form.control}
                            name="DeleteAfter"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Delete after x days (set 0 to keep event)</FormLabel>
                                <FormControl>
                                  <NumberInput
                                    value={field.value}
                                    onChange={(value) => field.onChange(Number(value))}
                                    ref={field.ref} />
                                </FormControl>
                              </FormItem>
                            )}
                          />


                        </div>
                        <div className='flex flex-wrap gap-y-8 gap-x-2.5 items-center mt-2'>
                          <FormField
                            control={form.control}
                            name={`Subtitle`}
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px]">
                                <FormLabel>Subtitle</FormLabel>
                                <FormControl >
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField

                            control={form.control}
                            name={`Info`}
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px]">
                                <FormLabel>Info</FormLabel>
                                <FormControl >
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className='flex flex-wrap gap-y-8 gap-x-2.5 items-center mt-2'>
                          <FormField
                            control={form.control}
                            name={`SupportEmail`}
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px] max-w-sm">
                                <FormLabel>Support Email</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`ContactEmail`}
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px] max-w-sm">
                                <FormLabel>Contact Email</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                        </div>
                        <div className=" mt-5 mb-5 ">
                          <div className="mb-5 border-b border-gray-300">Navigation Settings</div>
                          <FormField
                            control={form.control}
                            name="AllowNavigation"
                            render={({ field }) => (
                              <FormItem className="w-[10rem]">
                                <FormControl>
                                  <Checkbox
                                    checked={!!field.value}
                                    onCheckedChange={(checked) => field.onChange(checked)}
                                    label="Allow Navigation"
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <table className="w-[unset] border-separate border-spacing-y-2">
                            <tr>
                              <td> {(isNavigationEnabled || form.getValues("AcceptPrivacyCheckbox")) && (<FormField
                                control={form.control}
                                name="PrivacyType"
                                render={({ field }) => (
                                  <FormItem className="w-[11rem]">
                                    <FormLabel>Template</FormLabel>
                                    <FormControl>
                                      <Dropdown
                                        source={linkTypes}
                                        value={field.value}
                                        onChange={(value) => field.onChange(Number(value))}
                                        getLabel={(x) => x.Value}
                                        getValue={(x) => x.Id}
                                        searchable={true}
                                        disabled={false}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />)} </td>
                              <td className="pl-4">
                                {(isNavigationEnabled || form.getValues("AcceptPrivacyCheckbox")) && (form.getValues("PrivacyType")??0) > 0 && (<FormField
                                  control={form.control}
                                  name={`PrivacyLink`}
                                  render={({ field }) => (
                                    <FormItem className="flex-1 min-w-[380px] max-w-sm">
                                      <FormLabel>Privacy Link</FormLabel>
                                      <FormControl>
                                        {form.getValues("PrivacyType") === 1 ? (
                                        <Input {...field} />):
                                        ( 
                                           <Dropdown
                                        source={linkTemplates.filter(lt=>lt.Value =="PrivacyPartials")}
                                        value={field.value}
                                        onChange={(value) => field.onChange((value))}
                                        getLabel={(x) => x.Name}
                                        getValue={(x) => x.Name}
                                        searchable={true}
                                        disabled={false}
                                      />
                                      )}
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />)} 
                              </td>
                              <td className="pl-4">
                                 {(form.getValues("PrivacyType")??0) > 0 && (
                                <FormField
                                  control={form.control}
                                  name="AcceptPrivacyCheckbox"
                                  render={({ field }) => (
                                    <FormItem className="w-[10rem]">
                                      <FormControl>
                                        <Checkbox
                                          checked={!!field.value}
                                          onCheckedChange={(checked) => field.onChange(checked)}
                                          label="Require accepting privacy policy at login"
                                        />
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />)}

                              </td>
                            </tr>
{isNavigationEnabled && (
                            <tr>
                              <td> <FormField
                                control={form.control}
                                name="CorporateInfoType"
                                render={({ field }) => (
                                  <FormItem className="w-[11rem]">
                                    <FormLabel>Template</FormLabel>
                                    <FormControl>
                                      <Dropdown
                                        source={linkTypes}
                                        value={field.value}
                                        onChange={(value) => field.onChange(Number(value))}
                                        getLabel={(x) => x.Value}
                                        getValue={(x) => x.Id}
                                        searchable={true}
                                        disabled={false}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              /> </td>
                              <td className="pl-4">
                                {((form.getValues("CorporateInfoType")??0) > 0 )&& (

                                <FormField
                                  control={form.control}
                                  name={`CorporateInfoLink`}
                                  render={({ field }) => (
                                    <FormItem className="flex-1 min-w-[380px] max-w-sm">
                                      <FormLabel>Corporate Information Link</FormLabel>
                                      <FormControl>
                                        {form.getValues("CorporateInfoType") === 1 ? (
                                        <Input {...field} />):
                                        ( 
                                           <Dropdown
                                        source={linkTemplates.filter(lt=>lt.Value =="DisclaimerPartials")}
                                        value={field.value}
                                        onChange={(value) => field.onChange((value))}
                                        getLabel={(x) => x.Name}
                                        getValue={(x) => x.Name}
                                        searchable={true}
                                        disabled={false}
                                      />
                                      )}
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />)}
                              </td>
                            </tr>)}
                            {isNavigationEnabled && (
                            <tr>
                              <td> <FormField
                                control={form.control}
                                name="TermsOfUseType"
                                render={({ field }) => (
                                  <FormItem className="w-[11rem]">
                                    <FormLabel>Template</FormLabel>
                                    <FormControl>
                                      <Dropdown
                                        source={linkTypes}
                                        value={field.value}
                                        onChange={(value) => field.onChange(Number(value))}
                                        getLabel={(x) => x.Value}
                                        getValue={(x) => x.Id}
                                        searchable={true}
                                        disabled={false}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              /> </td>
                              <td className="pl-4">
                                {((form.getValues("TermsOfUseType")??0) > 0 )&& (
                                <FormField
                                  control={form.control}
                                  name={`TermsOfUseLink`}
                                  render={({ field }) => (
                                    <FormItem className="flex-1 min-w-[380px] max-w-sm">
                                      <FormLabel>Terms of Use Link</FormLabel>
                                       <FormControl>
                                        {form.getValues("TermsOfUseType") === 1 ? (
                                        <Input {...field} />):
                                        ( 
                                           <Dropdown
                                        source={linkTemplates.filter(lt=>lt.Value =="TouPartials")}
                                        value={field.value}
                                        onChange={(value) => field.onChange((value))}
                                        getLabel={(x) => x.Name}
                                        getValue={(x) => x.Name}
                                        searchable={true}
                                        disabled={false}
                                      />
                                      )}
                                      </FormControl>
                                    </FormItem>
                                  )}
                                />)}
                              </td>
                            </tr>)}
                          </table>
                        </div>
                      </div>
                    )}
                    {tabIndex == 1 && (
                      <div>
                        <div className='flex flex-wrap gap-y-8 gap-x-2.5 items-center mt-2'>
                          <FormField
                            control={form.control}
                            name="TemplateId"
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px] max-w-sm">
                                <FormLabel>Template</FormLabel>
                                <FormControl>
                                  <Dropdown
                                    source={templates}
                                    value={field.value}
                                    onChange={(value) => field.onChange(Number(value))}
                                    getLabel={(x) => x.Value}
                                    getValue={(x) => x.Id}
                                    searchable={true}
                                    disabled={false}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="LobbyTypeId"
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px] max-w-sm">
                                <FormLabel>LobbyType</FormLabel>
                                <FormControl>
                                  <Dropdown
                                    source={lobbyTypes}
                                    value={field.value}
                                    onChange={(value) => field.onChange(Number(value))}
                                    getLabel={(x) => x.Value}
                                    getValue={(x) => x.Id}
                                    searchable={true}
                                    disabled={false}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                               <FormField
                            control={form.control}
                            name="HideCountdown"
                            render={({ field }) => (
                              <FormItem className="flex items-center space-x-2">
                                <FormControl>
                                  <Toggle
                                    title="Hide Countdown"
                                    checked={!!field.value}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className='flex flex-wrap gap-y-8 gap-x-2.5 items-center mt-2'>
                          <FormField
                            control={form.control}
                            name="Banner"
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px]">
                                <FormLabel>Banner</FormLabel>
                                <FormControl>
                                  <div className="space-y-2">
                                    <button
                                      type="button"
                                      onClick={() => bannerInputRef.current?.click()}
                                      disabled={isUploadingBanner}
                                      className="relative block w-full max-w-xl overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
                                    >
                                      {field.value ? (
                                        <img
                                          src={field.value}
                                          alt="Banner"
                                          className="h-30 w-full rounded-md border object-cover cursor-pointer"
                                        />
                                      ) : (
                                        <div className="flex h-40 w-full items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground cursor-pointer">
                                          Click to upload banner
                                        </div>
                                      )}

                                      <div className="absolute inset-x-0 bottom-0 flex h-8 items-center justify-center bg-black/45 text-xs text-white">
                                        {isUploadingBanner ? "Uploading..." : "Click image to change banner"}
                                      </div>
                                    </button>
                                  </div>
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className='flex flex-wrap gap-y-8 gap-x-2.5 items-center mt-2'>

                          <FormField
                            control={form.control}
                            name="LogoLink"
                            render={({ field }) => (
                              <FormItem className="flex-1 min-w-[280px]">
                                <FormLabel>Logo link</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>

                       
                      </div>
                    )}
                    {tabIndex == 2 && (
                      <UserManagement
                        form={form}
                        apiFetch={apiFetch}
                        eventId={eventId}
                        onError={setError}

                      />
                    )}
                    {tabIndex == 3 && (
                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">

                        {featureItems.map((feature) => {
                          const enabled = feature.name === "EventTracking" ? false : !!eventFeature[feature.name];
                          return (
                            <div
                              key={feature.name}
                              className="flex h-32 flex-col items-center justify-between rounded-xl border border-blue-500 bg-slate-50 px-2 py-3 text-center"
                            >
                              <div className="text-xs font-medium uppercase tracking-wide text-slate-900 line-clamp-2">
                                {feature.label}
                              </div>

                              {feature.name === "EventTracking" ? (
                                <div>
                                  <OptionSet
                                    options={[...trackingOptions]}
                                    value={eventFeature.EventTrackingFull ? 2 : eventFeature.EventTrackingPartial ? 1 : 0}
                                    onChange={handleTrackingChange}
                                    name="event-tracking-mode"
                                    className="inline-flex flex-row gap-2"
                                  />
                                  {eventFeature.EventTrackingPartial && <div>Charts Only, no User Lists</div>}
                                </div>) : (
                                <Toggle
                                  checked={enabled}
                                  onChange={(e) =>
                                    handleFeatureToggle(feature.name, e.target.checked)
                                  }
                                />)}

                              {enabled && feature.hasSetup ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="min-w-16 text-xs uppercase"
                                  onClick={() => {

                                    setPopup(feature.name);

                                  }}
                                >
                                  Setup
                                </Button>
                              ) : enabled && feature.name === "WorldMap" ? (
                                <div className="text-gray-500">
                                  Will show as Source in Director
                                </div>
                              ) : (
                                <div className="h-8" />
                              )}

                                                                      {enabled && feature.name == "Plenum" && (
                                           <Checkbox
                                           className="mt-2"
                                    checked={!!eventFeature.UseCdn}
                                    onCheckedChange={(checked) => handleFeatureToggle("UseCdn", checked)}
                                    label="Use CDN for Presentations"
                                  />
                                        )}
                            </div>
                          );
                        })}

                        <div
                          key="OTHER_FEATURES"
                          className="flex h-32 flex-col items-center justify-between rounded-xl border border-blue-500 bg-slate-50 px-2 py-3 text-center"
                        >
                          <div className="text-xs font-medium uppercase tracking-wide text-slate-900 line-clamp-5">
                            For Networking, Showroom, WatchTogether, Marketplace and Workshop, please use the old setup tool.

                          </div>
                        </div>

                      </div>
                    )}
                    {tabIndex == 4 && (
                      <div className="mt-4 min-h-[500px] rounded-md border bg-white">
                        {isLoadingLinks ? (
                          <div className="p-4 text-sm text-slate-500">Loading links…</div>
                        ) : (
                          <iframe
                            title="Event links"
                            srcDoc={linksHtml}
                            sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                            className="min-h-[700px] w-full border-0"
                          />
                        )}
                      </div>
                    )}
                    {tabIndex == 5 && (
                      <TextVariables
                        apiFetch={apiFetch}
                        eventId={eventId}
                        onError={setError}

                      />
                    )}

                  </div>





                </div>


              </div>

             

            </form>
          </Form>


        </div>
      )}











      <PlenumSetupPopup
        isOpen={popupName == "Plenum"}
        onClose={() => setPopup("")}
        apiFetch={apiFetch}
        eventId={eventId ?? ""}

        onError={setError}
      />


      <AgendaSetupPopup
        isOpen={popupName == "Agenda"}
        onClose={() => setPopup("")}
        apiFetch={apiFetch}
        eventId={eventId ?? ""}

        onError={setError}
      />
      <DocumentCenterPopup
        isOpen={popupName == "DocumentCenter"}
        onClose={() => setPopup("")}
        apiFetch={apiFetch}
        eventId={eventId ?? ""}

        onError={setError}
      />

           <TextChatSetupPopup
        isOpen={popupName == "TextChat"}
        onClose={() => setPopup("")}
        apiFetch={apiFetch}
        eventId={eventId ?? ""}
        onError={setError}
      />

      <Popup
        isOpen={errorText != ""}
        onClose={() => setError("")}
        title="Error"
        className=""
      >
        <div>{errorText}</div>
      </Popup>


 <DeleteDataPopup
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        apiFetch={apiFetch}
        eventId={eventId ?? ""}

        onError={setError}
      />






    </div>

  )
}

